import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Lock, Mail, Heart, Briefcase, ShoppingBag, Trophy, Shield,
  ArrowRight, Eye, EyeOff, ChevronDown, Sparkles, Target, Award, Gift, Calendar,
  MapPin, Phone, Megaphone, Activity, LogOut, UserPlus, X,
} from "lucide-react";
import { TornEdge } from "../../components/sport/TornEdge";
import ParallaxBull from "../../components/sport/ParallaxBull";
import JerseyBackBadge from "../../components/sport/JerseyBackBadge";
import LicenseeDashboard, { LicenseeMiniCard } from "../../components/sport/LicenseeDashboard";
import PlayerProDashboard from "../../components/sport/PlayerProDashboard";
import PartnerConventionsCard from "../../components/sport/PartnerConventionsCard";
import EditProfileModal from "../../components/sport/EditProfileModal";
import MatchPronoModal from "../../components/sport/MatchPronoModal";
import { Check } from "lucide-react";
import { useCustomerAuth } from "../../lib/useCustomerAuth";
import { useFeatureGate } from "../../lib/useFeatureGate";
import { api } from "../../lib/api";
import { toast } from "sonner";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";

// Espace types — informational, the actual access is auto-detected from the user's tags after login
const SPACES = [
  { id: "client", icon: ShoppingBag, label: "Espace Client", desc: "Suivi commandes boutique, factures, billets achetés.", color: "#a78bfa" },
  { id: "licensee", icon: Award, label: "Espace Licencié", desc: "Carte digitale, offres préférentielles, pack saison.", color: "#06b6d4" },
  { id: "supporter", icon: Heart, label: "Espace Supporter", desc: "Adhésion, avantages, événements supporters, communauté.", color: "#ec4899" },
  { id: "partner", icon: Briefcase, label: "Espace Partenaire", desc: "Annuaire partenaires, contrats, factures business, accès VIP.", color: "#f59e0b" },
  { id: "player", icon: Trophy, label: "Espace Joueur", desc: "Planning entraînement, vidéos, stats personnelles, communauté.", color: "#10b981" },
];

export default function SportMonEspace() {
  const { customer, login, register, logout } = useCustomerAuth();
  const [tab, setTab] = useState("login"); // login | register
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Reset form when tab toggles
  useEffect(() => { setError(""); }, [tab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
        toast.success("Connexion réussie !");
      } else {
        await register({ first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone, password: form.password });
        toast.success("Compte créé !");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || (tab === "login" ? "Identifiants incorrects" : "Erreur lors de la création du compte"));
    } finally {
      setBusy(false);
    }
  };

  // Auto-detect space from customer's tags after login
  const detectSpace = () => {
    const tags = ((customer || {}).tags || []).map((t) => (t || "").toLowerCase());
    const get = (id) => SPACES.find((s) => s.id === id) || SPACES[0];
    if (tags.includes("partner") || tags.includes("partenaire")) return get("partner");
    if (tags.includes("player") || tags.includes("joueur")) return get("player");
    if (tags.includes("licensee") || tags.includes("licencié") || customer?.is_licensee) return get("licensee");
    if (tags.includes("supporter")) return get("supporter");
    return get("client");
  };

  return (
    <div style={{ background: BG_DARK }}>
      {/* HERO compact (réduit ×3) */}
      <section className="relative pt-24 pb-6 sm:pt-28 sm:pb-8 overflow-hidden flex items-center">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0" style={{
            background: `linear-gradient(180deg, ${BG_DARK} 0%, ${PRIMARY} 60%, ${BG_DARK} 100%)`,
          }}/>
          <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
            backgroundImage: `radial-gradient(circle, ${SECONDARY} 1px, transparent 1px)`,
            backgroundSize: "16px 16px",
            maskImage: "radial-gradient(ellipse at center, black, transparent 75%)",
          }}/>
        </div>
        {/* Parallax bull background — réduit aussi */}
        <ParallaxBull direction="ltr" height={140} opacity={0.06}/>
        <div className="relative z-[2] max-w-5xl mx-auto px-4 lg:px-8 text-center text-white w-full">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="text-pink-300 font-black tracking-[0.3em] text-[10px] uppercase mb-1.5">
            Connexion · Inscription
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-black uppercase italic" style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.5rem)", lineHeight: 1 }}>
            Mon <span style={{ color: SECONDARY }}>Espace</span>
          </motion.h1>
        </div>
      </section>

      {/* CONTENT */}
      <section className="relative py-6 sm:py-8">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          {customer ? (
            <LoggedInDashboard customer={customer} space={detectSpace()} onLogout={() => { logout(); toast.info("Déconnecté"); }}/>
          ) : (
            <div className="grid lg:grid-cols-[1.1fr,1fr] gap-10 items-start">
              {/* FORM */}
              <div className="rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden">
                <div className="grid grid-cols-2">
                  <button data-testid="tab-login"
                    onClick={() => setTab("login")}
                    className={`py-4 font-black uppercase tracking-widest text-xs transition ${tab === "login" ? "text-white" : "text-slate-500 hover:text-slate-700 bg-slate-50"}`}
                    style={tab === "login" ? { background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` } : {}}>
                    Connexion
                  </button>
                  <button data-testid="tab-register"
                    onClick={() => setTab("register")}
                    className={`py-4 font-black uppercase tracking-widest text-xs transition ${tab === "register" ? "text-white" : "text-slate-500 hover:text-slate-700 bg-slate-50"}`}
                    style={tab === "register" ? { background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` } : {}}>
                    Créer un compte
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-4" data-testid="auth-form">
                  <AnimatePresence mode="wait">
                    {tab === "register" && (
                      <motion.div key="register-fields" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                        className="grid grid-cols-2 gap-3 overflow-hidden">
                        <div>
                          <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Prénom *</label>
                          <input data-testid="input-firstname" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                            className="mt-1 w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                        </div>
                        <div>
                          <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Nom *</label>
                          <input data-testid="input-lastname" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                            className="mt-1 w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <div>
                    <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Email *</label>
                    <div className="relative mt-1">
                      <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
                      <input data-testid="input-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full pl-11 pr-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                    </div>
                  </div>
                  {tab === "register" && (
                    <div>
                      <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Téléphone</label>
                      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="mt-1 w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                    </div>
                  )}
                  <div>
                    <label className="text-pink-600 font-black text-[11px] uppercase tracking-widest">Mot de passe *</label>
                    <div className="relative mt-1">
                      <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
                      <input data-testid="input-password" type={showPwd ? "text" : "password"} required minLength={6}
                        value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="w-full pl-11 pr-11 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                      <button type="button" onClick={() => setShowPwd(!showPwd)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                        {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
                      </button>
                    </div>
                    {tab === "register" && <div className="text-[10px] text-slate-400 mt-1">Au moins 6 caractères</div>}
                  </div>
                  {error && <div className="rounded-lg bg-rose-50 text-rose-700 text-sm px-4 py-2.5">{error}</div>}
                  <button data-testid="auth-submit" type="submit" disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-black uppercase text-sm tracking-widest text-white shadow-2xl hover:scale-[1.02] transition disabled:opacity-60"
                    style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                    {busy ? "..." : tab === "login" ? "Se connecter" : "Créer mon compte"}
                    <ArrowRight size={14}/>
                  </button>
                  <div className="text-center text-[12px] text-slate-500">
                    {tab === "login" ? (
                      <>Pas encore de compte ? <button type="button" onClick={() => setTab("register")} className="text-pink-600 font-black underline">Inscription</button></>
                    ) : (
                      <>Déjà un compte ? <button type="button" onClick={() => setTab("login")} className="text-pink-600 font-black underline">Connectez-vous</button></>
                    )}
                  </div>
                </form>

              </div>

              {/* SPACES INFO */}
              <div className="space-y-4">
                <div className="text-pink-600 font-black tracking-[0.3em] text-[11px] uppercase">Un seul compte, plusieurs espaces</div>
                <h3 className="font-black text-2xl sm:text-3xl uppercase italic" style={{ color: PRIMARY }}>
                  Votre <span style={{ color: SECONDARY }}>tableau de bord</span><br/>s'adapte à vous
                </h3>
                <p className="text-slate-600 text-sm">Selon votre statut, vous accédez automatiquement à votre espace dédié après connexion.</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  {SPACES.map((s, i) => (
                    <motion.div key={s.id}
                      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                      className="rounded-2xl p-4 bg-white shadow-md border border-slate-100 hover:border-pink-300 hover:shadow-xl transition group">
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white shadow mb-2"
                        style={{ background: `linear-gradient(135deg, ${s.color}, ${PRIMARY})` }}>
                        <s.icon size={16}/>
                      </div>
                      <div className="font-black uppercase text-sm" style={{ color: PRIMARY }}>{s.label}</div>
                      <div className="text-slate-500 text-[12px] mt-1">{s.desc}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function LoggedInDashboard({ customer, space, onLogout }) {
  const tags = ((customer || {}).tags || []).map((t) => (t || "").toLowerCase());
  const isPartner = tags.includes("partner") || customer?.is_partner === true;
  const isPlayer = tags.includes("player");
  const isLicensee = tags.includes("licensee") || customer?.is_licensee === true;
  const [profileOpen, setProfileOpen] = useState(false);
  const [pseudoOpen, setPseudoOpen] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null); // "avatar" | "partner-logo" | null
  const [pseudoState, setPseudoState] = useState({
    display_name: customer?.display_name || "",
    display_name_pending: customer?.display_name_pending || "",
    display_name_status: customer?.display_name_status || "none",
  });
  const { refreshCustomer } = useCustomerAuth();
  const avatarInputRef = React.useRef(null);
  const logoInputRef = React.useRef(null);

  const handleImageUpload = async (file, kind) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp|gif)$/i.test(file.type)) {
      toast.error("Format non supporté (jpg / png / webp / gif uniquement)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 8 Mo)");
      return;
    }
    setUploadingKind(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const token = localStorage.getItem("msc_customer_token");
      await api.post("/customer/me/upload-image", fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      await refreshCustomer?.();
      toast.success(kind === "avatar" ? "Photo de profil mise à jour" : "Logo de l'entreprise mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploadingKind(null);
    }
  };

  useEffect(() => {
    setPseudoState({
      display_name: customer?.display_name || "",
      display_name_pending: customer?.display_name_pending || "",
      display_name_status: customer?.display_name_status || "none",
    });
  }, [customer?.display_name, customer?.display_name_pending, customer?.display_name_status]);

  // Feature gates DigiPilote — chaque widget se cache automatiquement si le
  // module correspondant est désactivé dans la Console widgets. Un club qui
  // gère ses conventions via un outil tiers peut couper sport_conventions_enabled
  // et la section disparait de Mon Espace partenaire.
  const pronosOn = useFeatureGate("sport_match_pronos_enabled", "sport_supporters_enabled");
  const conventionsOn = useFeatureGate("sport_conventions_enabled");
  const licenseeOffersOn = useFeatureGate("sport_licensee_offers_enabled");
  const shopOn = useFeatureGate("shop_enabled");
  const playersRosterOn = useFeatureGate("sport_players_roster_enabled");
  const partnersDirectoryOn = useFeatureGate("sport_partners_directory_enabled");

  // Salutation contextuelle
  const hour = new Date().getHours();
  const greeting = hour < 5 ? "Bonne nuit" : hour < 12 ? "Bonjour" : hour < 18 ? "Bonjour" : "Bonsoir";
  const firstName = customer.first_name || customer.email?.split("@")[0] || "";
  const initials = ((customer.first_name?.charAt(0) || "") + (customer.last_name?.charAt(0) || "")).toUpperCase() || (customer.email?.charAt(0) || "?").toUpperCase();
  const avatarUrl = customer.avatar_url; // visage représentant (ou supporter)
  const companyLogo = customer.company_logo;
  const company = isPartner ? (customer.company_name || "") : "";
  const accent = isPartner ? "#f59e0b" : space.color;

  // Maillot floqué — partenaires (nom entreprise) ET supporters (pseudo ou prénom). Pas pour joueur pro.
  // Si le nom de l'entreprise dépasse 24 caractères, on bascule sur le prénom du membre (illisible sinon).
  const showJersey = !isPlayer;
  const supporterDisplayName = (customer.display_name || "").trim();
  const partnerJerseyName = company && company.length <= 24 ? company : firstName;
  const jerseyName = isPartner
    ? partnerJerseyName
    : (supporterDisplayName || firstName);

  // Mini-météo engagement — stats live + CTA contextuel
  const [meteo, setMeteo] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem("msc_customer_token");
    if (!token) return;
    const cfg = { headers: { Authorization: `Bearer ${token}` } };
    if (isPartner) {
      api.get("/sport/business/offers/me/requests", cfg)
        .then((r) => {
          const rows = r.data?.rows || [];
          const total = rows.length;
          const pending = rows.filter((x) => x.status === "pending").length;
          if (pending > 0) {
            setMeteo({ icon: "📜", primary: `${pending} convention${pending > 1 ? "s" : ""} en attente`, accent: "#f59e0b", cta: "Mes conventions", target: "conventions" });
          } else if (total > 0) {
            setMeteo({ icon: "✅", primary: `${total} convention${total > 1 ? "s" : ""} active${total > 1 ? "s" : ""}`, accent: "#10b981", cta: "Mes conventions", target: "conventions" });
          } else {
            setMeteo({ icon: "✨", primary: "Construire ma demande de partenariat", accent: SECONDARY, cta: "Voir les offres", target: "offers" });
          }
        }).catch(() => {});
    } else if (!isPlayer) {
      api.get("/match-live/me/participations", cfg)
        .then((r) => {
          const s = r.data?.stats || {};
          if (s.prizes_won_count > 0) {
            setMeteo({ icon: "🏆", primary: `${s.prizes_won_count} lot${s.prizes_won_count > 1 ? "s" : ""} à récupérer`, accent: "#f59e0b", cta: "Récupérer mon lot", target: "participations" });
          } else if (s.matches_followed > 0) {
            setMeteo({ icon: "🔥", primary: `${s.matches_followed} match${s.matches_followed > 1 ? "s" : ""} suivi${s.matches_followed > 1 ? "s" : ""}`, accent: SECONDARY, cta: "Mes participations", target: "participations" });
          } else {
            setMeteo({ icon: "🎯", primary: "Je fais mon 1er prono !", accent: SECONDARY, cta: "Lancer", target: "home-prono" });
          }
        }).catch(() => {});
    }
  }, [isPartner, isPlayer]);

  const handleMeteoClick = () => {
    if (!meteo?.target) return;
    if (meteo.target === "home-prono") {
      // Redirige vers la home avec query param qui auto-ouvre le modal de prono prochain match
      window.location.href = "/sport?openProno=1#dashboard-next-match";
      return;
    }
    const targets = {
      conventions: "partner-business-hub",
      offers: "partner-business-hub",
      participations: "supporter-participations",
    };
    const id = targets[meteo.target];
    if (id) {
      const el = document.querySelector(`[data-testid="${id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        const wantedTab = meteo.target === "conventions" ? "conventions" : meteo.target === "offers" ? "offers" : null;
        if (wantedTab) {
          setTimeout(() => {
            const tabBtn = document.querySelector(`[data-testid="biz-tab-${wantedTab}"]`);
            tabBtn?.click();
          }, 350);
        }
      }
    }
  };

  return (
    <div className="space-y-8 sm:space-y-10">
      {/* Compact greeting strip — remplace la grosse bannière hero */}
      <div className="relative rounded-2xl mb-4 sm:mb-6 px-4 sm:px-6 py-5 sm:py-6 flex items-center gap-4 sm:gap-5 shadow-md"
        style={{ background: `linear-gradient(135deg, ${accent}15, ${PRIMARY}10)`, border: `1px solid ${accent}40`, minHeight: showJersey ? 200 : undefined, overflow: showJersey ? "visible" : "hidden" }}>
        {/* Maillot dos floqué — DESKTOP/TABLET seulement (mobile : déplacé sous le strip pour éviter le chevauchement avec MON ESPACE / BONJOUR LUCAS) */}
        {showJersey && (
          <JerseyBackBadge
            companyName={jerseyName}
            number={13}
            className="hidden sm:block absolute right-2 sm:right-6 lg:right-10 w-48 sm:w-64 lg:w-80 z-0"
            style={{ top: "-30%" }}
          />
        )}

        {/* Mini carte de licence — DESKTOP : dans le strip, inclinée, ouvre modal au clic */}
        {isLicensee && !isPartner && (
          <div className="hidden md:block absolute z-20 pointer-events-auto"
            style={{
              top: "50%",
              right: "clamp(280px, 28%, 380px)",
              width: "clamp(110px, 11vw, 145px)",
              transform: "translateY(-50%)",
            }}>
            <LicenseeMiniCard/>
          </div>
        )}

        {/* Avatar(s) — agrandi ×3, double pour partenaire (logo + représentant) */}
        <div className={`relative flex items-center flex-shrink-0 z-10 ${isPartner && companyLogo ? "-space-x-5 sm:-space-x-6" : ""}`}>
          {isPartner && (
            <div className="relative group">
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden bg-white shadow-2xl border-4 border-white flex items-center justify-center"
                data-testid="partner-company-logo">
                {companyLogo ? (
                  <img src={companyLogo} alt={company} className="w-full h-full object-cover"/>
                ) : (
                  <div className="text-slate-300 text-[10px] uppercase tracking-widest text-center px-2">Aucun logo</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingKind === "partner-logo"}
                className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-white shadow-lg border-2 border-white flex items-center justify-center hover:scale-110 transition disabled:opacity-60"
                style={{ background: SECONDARY }}
                title="Modifier le logo de l'entreprise"
                data-testid="upload-partner-logo-btn"
              >
                {uploadingKind === "partner-logo" ? (
                  <span className="text-white text-[10px] animate-pulse">…</span>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                )}
              </button>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleImageUpload(f, "partner-logo"); }}
                data-testid="upload-partner-logo-input"
              />
            </div>
          )}
          <div className="relative group">
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden flex items-center justify-center shadow-2xl"
              style={{ background: `linear-gradient(135deg, ${accent}, ${PRIMARY})`, border: `4px solid ${accent}` }}>
              {avatarUrl ? (
                <img src={avatarUrl} alt={firstName} className="w-full h-full object-cover"/>
              ) : (
                <span className="text-white font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.5rem, 8vw, 4rem)", letterSpacing: "0.05em" }}>
                  {initials}
                </span>
              )}
            </div>
            {/* Bouton modifier photo */}
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploadingKind === "avatar"}
              className="absolute bottom-1 right-1 w-10 h-10 sm:w-11 sm:h-11 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition disabled:opacity-60"
              style={{ background: accent, border: "3px solid white" }}
              title="Modifier ma photo"
              data-testid="upload-avatar-btn"
            >
              {uploadingKind === "avatar" ? (
                <span className="text-white text-xs animate-pulse">…</span>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
              )}
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleImageUpload(f, "avatar"); }}
              data-testid="upload-avatar-input"
            />
          </div>
        </div>

        {/* Texte */}
        <div className={`flex-1 min-w-0 z-10 ${isPartner ? "pr-32 sm:pr-44 lg:pr-52" : ""}`}>
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.25em] text-white/70 font-bold">{greeting},</div>
          <div className="font-black text-white truncate" style={{ fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 6vw, 3.5rem)", lineHeight: 1, letterSpacing: "0.04em" }}>
            {firstName}
          </div>
          {/* Pseudo public (modération) — uniquement pour les supporters/licenciés */}
          {!isPartner && !isPlayer && (
            <button
              type="button"
              onClick={() => setPseudoOpen(true)}
              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/25 text-white text-[10px] sm:text-xs font-bold transition group"
              data-testid="open-pseudo-edit"
            >
              {pseudoState.display_name_pending ? (
                <>
                  <span className="opacity-75">@{pseudoState.display_name_pending}</span>
                  <span className="text-amber-300 text-[9px] uppercase tracking-wider">· en attente</span>
                </>
              ) : pseudoState.display_name ? (
                <>@{pseudoState.display_name}</>
              ) : (
                <span className="text-pink-200">+ Choisir un pseudo</span>
              )}
              <Sparkles size={10} className="opacity-60 group-hover:opacity-100 transition"/>
            </button>
          )}
          {company && (
            <div className="text-xs sm:text-sm text-white/60 uppercase tracking-wider truncate mt-1">{company}</div>
          )}
        </div>

        {/* (Mini-météo déplacée hors du strip, voir plus bas — bouton centré PC + mobile) */}

        {/* Bouton déconnexion — toujours en HAUT À DROITE de l'espace compte (au-dessus du maillot) */}
        <button data-testid="logout-btn" onClick={onLogout}
          className="hidden sm:inline-flex absolute top-3 right-3 z-30 items-center gap-1.5 text-[10px] uppercase tracking-wider text-white font-black px-3 py-1.5 rounded-full bg-black/40 hover:bg-pink-600 backdrop-blur-md border border-white/20 transition shadow-lg">
          <LogOut size={12}/> Déconnexion
        </button>
        {/* Mobile : juste un bouton compact en haut à droite */}
        <button data-testid="logout-btn-mobile" onClick={onLogout}
          aria-label="Déconnexion"
          className="sm:hidden absolute top-3 right-3 z-30 inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/40 hover:bg-pink-600 backdrop-blur-md border border-white/20 text-white font-black text-sm transition shadow-lg">
          <LogOut size={15}/>
        </button>
      </div>

      {/* Bloc MOBILE dédié — maillot dos perso + carte digitale 3D côte à côte
          Affiché uniquement sur mobile (<sm) car ces éléments sont positionnés
          en absolu dans le strip sur écrans plus larges. Évite le chevauchement
          avec MON ESPACE / BONJOUR {prénom} signalé sur mobile. */}
      {(showJersey || (isLicensee && !isPartner)) && (
        <div className="sm:hidden flex items-center justify-center gap-4 mb-5 px-2"
          data-testid="mobile-jersey-card-row">
          {showJersey && (
            <div className="flex-shrink-0 w-32"
              style={{ filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.45))" }}>
              <JerseyBackBadge
                companyName={jerseyName}
                number={13}
                className="w-full"
              />
            </div>
          )}
          {isLicensee && !isPartner && (
            <div className="flex-shrink-0 w-24">
              <LicenseeMiniCard className="w-full"/>
            </div>
          )}
        </div>
      )}

      {/* Mini-météo engagement — placé sous le strip, CENTRÉ (PC + mobile), plus de chevauchement avec le maillot */}
      {meteo && (
        <div className="flex justify-center mb-5">
          <button
            type="button"
            onClick={handleMeteoClick}
            className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full active:scale-95 hover:scale-105 transition group cursor-pointer shadow-md"
            style={{ background: `${meteo.accent}20`, border: `1px solid ${meteo.accent}60` }}
            data-testid="meteo-engagement">
            <span className="text-base">{meteo.icon}</span>
            <span className="text-[11px] sm:text-xs uppercase tracking-wider font-black" style={{ color: meteo.accent }}>
              {meteo.primary}
            </span>
            {meteo.cta && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-black px-2.5 py-1 rounded-full text-white group-hover:gap-1.5 transition-all"
                style={{ background: meteo.accent }}>
                {meteo.cta} <ArrowRight size={11}/>
              </span>
            )}
          </button>
        </div>
      )}

      {/* PARTENAIRE BUSINESS — sections dédiées (Réseau / Offres / Agenda) */}
      {isPartner && <PartnerBusinessHub/>}

      {/* PARTENAIRE — Conventions de partenariat (à signer / soldées) */}
      {isPartner && conventionsOn && <PartnerConventionsCard/>}

      {/* LICENCIÉ — carte digitale + offres préférentielles partenaires */}
      {isLicensee && !isPartner && <LicenseeDashboard hideOffers={!licenseeOffersOn}/>}

      {/* JOUEUR PRO — prochain match + planning équipe + raccourcis */}
      {isPlayer && playersRosterOn && <PlayerProDashboard customer={customer}/>}

      {/* MES PARTICIPATIONS — pronos, votes, lots gagnés (tous sauf joueur pro) */}
      {!isPlayer && pronosOn && <UpcomingPronosBoard/>}
      {!isPlayer && pronosOn && <SupporterParticipations/>}

      {/* Quick links — adaptés au rôle */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shopOn && <DashCard icon={ShoppingBag} title="Mes commandes" desc="Boutique, billetterie." to="/sport/boutique"/>}
        {!isPartner && (
          <DashCard icon={Heart} title="Mes adhésions" desc="Statut supporter, avantages." to="/sport/supporters"/>
        )}
        {isPartner && partnersDirectoryOn && (
          <DashCard icon={Briefcase} title="Mes contrats partenaire" desc="Visibilité, factures." to="/sport/partenaires"/>
        )}
        <DashCard icon={Trophy} title="Le club" desc="Histoire, palmarès, équipe." to="/sport/club/histoire"/>
        <DashCard icon={Sparkles} title="Mes coordonnées" desc="Modifier mon profil." onClick={() => setProfileOpen(true)} testId="dashcard-mes-coordonnees" />
        <DashCard icon={Shield} title="Sécurité" desc="Mot de passe, sessions." to="#"/>
      </div>

      {/* Modal édition coordonnées */}
      <EditProfileModal open={profileOpen} onClose={() => setProfileOpen(false)}/>

      {/* Modal édition pseudo (modération) */}
      {pseudoOpen && (
        <PseudoEditModal
          current={pseudoState.display_name}
          pending={pseudoState.display_name_pending}
          status={pseudoState.display_name_status}
          onClose={() => setPseudoOpen(false)}
          onSubmitted={(newPending) => {
            setPseudoState((p) => ({ ...p, display_name_pending: newPending, display_name_status: "pending" }));
            setPseudoOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// PseudoEditModal — Permet au supporter de demander un nouveau pseudo
// Soumis à modération admin. Bloque les soumissions répétées.
// ---------------------------------------------------------------
function PseudoEditModal({ current, pending, status, onClose, onSubmitted }) {
  const [value, setValue] = useState(current || "");
  const [submitting, setSubmitting] = useState(false);
  const hasPending = !!pending;
  const isRejected = status === "rejected";

  const submit = async (e) => {
    e?.preventDefault();
    const v = (value || "").trim();
    if (v.length < 3) {
      toast.error("Le pseudo doit faire au moins 3 caractères.");
      return;
    }
    if (v.length > 24) {
      toast.error("Le pseudo doit faire au maximum 24 caractères.");
      return;
    }
    if (!/^[A-Za-z0-9_\-.]+$/.test(v)) {
      toast.error("Caractères autorisés : lettres, chiffres, _ - .");
      return;
    }
    setSubmitting(true);
    try {
      await api.put("/customer/me/profile-public", { display_name: v });
      toast.success("Pseudo envoyé en modération ! 🛡️");
      onSubmitted?.(v);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} data-testid="pseudo-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-100 flex items-start gap-3 bg-gradient-to-r from-violet-50 to-pink-50">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-pink-500 text-white flex items-center justify-center shrink-0">
            <Sparkles size={18}/>
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-violet-700 font-bold">Pseudo supporter</div>
            <h3 className="text-lg font-black text-slate-900">Choisir votre pseudo</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Visible publiquement dans les leaderboards & pronos. Validé par un modérateur.</p>
          </div>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {current && (
            <div className="text-xs text-slate-500">
              Pseudo actuel : <span className="font-bold text-slate-700">@{current}</span>
            </div>
          )}
          {hasPending && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              ⏳ <strong>@{pending}</strong> est en attente de modération. Vous pouvez en proposer un autre, il remplacera celui en attente.
            </div>
          )}
          {isRejected && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
              ❌ Votre pseudo précédent a été refusé. Essayez quelque chose de plus respectueux.
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1.5">Nouveau pseudo</label>
            <div className="flex items-center gap-2">
              <span className="text-slate-400 font-black">@</span>
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="MonPseudo94"
                maxLength={24}
                className="flex-1 h-11 px-3 rounded-lg border-2 border-slate-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none font-bold"
                autoFocus
                data-testid="pseudo-input"
              />
            </div>
            <div className="text-[10px] text-slate-400 mt-1">3 à 24 caractères · lettres, chiffres, _ - .</div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm transition">
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || !value.trim() || value.trim() === current}
              className="px-5 py-2 rounded-full bg-gradient-to-r from-violet-600 to-pink-500 text-white font-bold text-sm shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
              data-testid="pseudo-submit"
            >
              {submitting ? "Envoi…" : "Envoyer en modération"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// UPCOMING PRONOS BOARD — pronostics sur plusieurs matchs à venir
// ---------------------------------------------------------------
function UpcomingPronosBoard() {
  const [matches, setMatches] = useState([]);
  const [myPronos, setMyPronos] = useState({}); // { match_id: { home_pred, away_pred } }
  const [openMatch, setOpenMatch] = useState(null);

  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem("msc_customer_token");
    const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    api.get("/sport/upcoming-matches?limit=5")
      .then((r) => r.data?.matches || [])
      .then(async (rows) => {
        if (!alive) return;
        setMatches(rows);
        // Pour chaque match, vérifie si un prono existe déjà
        const out = {};
        await Promise.all(rows.map(async (m) => {
          try {
            const r = await api.get(`/match-live/prono/mine?match_id=${encodeURIComponent(m.id)}`, cfg);
            if (r.data?.prono) out[m.id] = r.data.prono;
          } catch {}
        }));
        if (alive) setMyPronos(out);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (matches.length === 0) return null;

  const fmtMatchDate = (iso, time) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const day = d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" });
      return `${day}${time ? " · " + time : ""}`;
    } catch { return iso; }
  };

  const computeKickoff = (m) => {
    if (!m.date_iso) return null;
    try {
      const base = new Date(m.date_iso);
      if (m.time && /^\d{1,2}:\d{2}/.test(m.time)) {
        const [hh, mm] = m.time.split(":");
        base.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
      }
      return base.toISOString();
    } catch { return null; }
  };

  return (
    <section className="space-y-4 mt-4" data-testid="upcoming-pronos-board">
      <div className="flex items-center gap-2.5 px-1">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: SECONDARY }}>
          <Target size={18} className="text-white"/>
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-black uppercase tracking-wider text-base sm:text-lg text-white leading-tight">
            Mes pronostics
          </h2>
          <p className="text-xs sm:text-sm text-white/60 font-bold leading-tight">
            {matches.length} match{matches.length > 1 ? "s" : ""} à venir
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {matches.map((m) => {
          const mine = myPronos[m.id];
          const kickoff = computeKickoff(m);
          const isLocked = kickoff && new Date(kickoff).getTime() <= Date.now();
          return (
            <div key={m.id} className="rounded-2xl bg-white border border-slate-100 shadow-md p-4 flex items-center gap-3"
              data-testid={`prono-row-${m.id}`}>
              {/* Logos + équipes */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <img src={m.home_logo} alt="" className="w-9 h-9 rounded-full object-contain bg-slate-50 ring-1 ring-slate-200 flex-shrink-0"/>
                <div className="text-[10px] font-black uppercase text-slate-700 truncate flex-1">{m.home_team}</div>
                <div className="text-[10px] font-black text-slate-400">VS</div>
                <div className="text-[10px] font-black uppercase text-slate-700 truncate flex-1 text-right">{m.away_team}</div>
                <img src={m.away_logo} alt="" className="w-9 h-9 rounded-full object-contain bg-slate-50 ring-1 ring-slate-200 flex-shrink-0"/>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  {fmtMatchDate(m.date_iso, m.time)}
                </div>
                {mine ? (
                  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-wider"
                    data-testid={`prono-done-${m.id}`}>
                    <Check size={10}/> Mon prono : {mine.home_pred}–{mine.away_pred}
                  </div>
                ) : isLocked ? (
                  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-wider">
                    Verrouillé
                  </div>
                ) : (
                  <button
                    onClick={() => setOpenMatch(m)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-white text-[10px] font-black uppercase tracking-wider shadow-md hover:scale-105 transition"
                    style={{ background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` }}
                    data-testid={`prono-btn-${m.id}`}
                  >
                    <Target size={10}/> Pronostiquer
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal partagée pour tous les matchs */}
      {openMatch && (
        <MatchPronoModal
          match={openMatch}
          kickoffIso={computeKickoff(openMatch)}
          onClose={async () => {
            const closedId = openMatch.id;
            setOpenMatch(null);
            // Refetch ce prono spécifique pour mettre à jour l'UI
            const token = localStorage.getItem("msc_customer_token");
            const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            try {
              const r = await api.get(`/match-live/prono/mine?match_id=${encodeURIComponent(closedId)}`, cfg);
              if (r.data?.prono) setMyPronos((prev) => ({ ...prev, [closedId]: r.data.prono }));
            } catch {}
          }}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------
// MES PARTICIPATIONS — historique pronos / votes / lots gagnés / concours
// ---------------------------------------------------------------
function SupporterParticipations() {
  const [data, setData] = useState({ stats: {}, pronos: [], votes: [], prizes: [] });
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pronos");

  useEffect(() => {
    const token = localStorage.getItem("msc_customer_token");
    const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    Promise.all([
      api.get("/match-live/me/participations", cfg).then((r) => r.data).catch(() => ({})),
      api.get("/match-live/contests/active").then((r) => r.data?.rows || []).catch(() => []),
    ]).then(([part, c]) => {
      setData(part || {});
      setContests(c || []);
      setLoading(false);
    });
  }, []);

  const stats = data.stats || {};
  const StatCard = ({ Icon, label, value, color }) => (
    <div className="rounded-2xl p-4 border border-slate-100 bg-white shadow-md">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${color}20`, color }}>
          <Icon size={20}/>
        </div>
        <div>
          <div className="text-2xl font-black tabular-nums" style={{ color: PRIMARY, fontFamily: "'Bebas Neue', sans-serif" }}>{value || 0}</div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mb-8" data-testid="supporter-participations">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-1 h-8 rounded-full" style={{ background: SECONDARY }}/>
        <div>
          <h3 className="font-black uppercase italic text-2xl sm:text-3xl text-white" style={{ fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>
            Mes participations
          </h3>
          <div className="text-white/60 text-sm">Historique de vos pronos, votes et lots gagnés.</div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard Icon={Calendar} label="Matchs suivis" value={stats.matches_followed} color={PRIMARY}/>
        <StatCard Icon={Target} label="Pronostics" value={stats.pronos_count} color={SECONDARY}/>
        <StatCard Icon={Award} label="Votes MVP" value={stats.votes_count} color="#f59e0b"/>
        <StatCard Icon={Gift} label="Lots gagnés" value={stats.prizes_won_count} color="#10b981"/>
      </div>

      {/* Lots gagnés en avant */}
      {data.prizes && data.prizes.length > 0 && (
        <div className="mb-5 rounded-2xl p-5 border-2 border-yellow-300/60"
          style={{ background: `linear-gradient(135deg, #FFD70015, #FF8C0010)` }} data-testid="supporter-prizes">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={20} className="text-yellow-600"/>
            <div className="font-black uppercase text-yellow-700 tracking-widest text-sm">Vos lots gagnés</div>
          </div>
          <div className="space-y-2">
            {data.prizes.map((p, i) => (
              <div key={i} className="rounded-xl p-3 bg-white/95 border border-yellow-300/40 flex items-center gap-3">
                {p.sponsor_logo && <img src={p.sponsor_logo} alt={p.sponsor} className="h-10 w-10 object-contain bg-white rounded-lg p-1 border border-slate-200"/>}
                <div className="flex-1 min-w-0">
                  <div className="font-black text-slate-800 truncate">{p.prize}</div>
                  <div className="text-xs text-slate-500 truncate">{p.match_label}</div>
                  {p.sponsor && <div className="text-[10px] text-slate-400 uppercase tracking-wider">offert par {p.sponsor}</div>}
                </div>
                <div className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-green-100 text-green-700 font-bold whitespace-nowrap">À récupérer</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-3">
        <button onClick={() => setTab("pronos")} data-testid="part-tab-pronos"
          className={`flex-1 px-4 py-2.5 rounded-xl font-black uppercase tracking-wider text-xs transition ${tab === "pronos" ? "text-white shadow-lg" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
          style={tab === "pronos" ? { background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` } : {}}>
          <Target size={13} className="inline mr-1.5 -mt-0.5"/> Mes pronos ({data.pronos?.length || 0})
        </button>
        <button onClick={() => setTab("votes")} data-testid="part-tab-votes"
          className={`flex-1 px-4 py-2.5 rounded-xl font-black uppercase tracking-wider text-xs transition ${tab === "votes" ? "text-white shadow-lg" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
          style={tab === "votes" ? { background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` } : {}}>
          <Award size={13} className="inline mr-1.5 -mt-0.5"/> Mes votes MVP ({data.votes?.length || 0})
        </button>
        <button onClick={() => setTab("contests")} data-testid="part-tab-contests"
          className={`flex-1 px-4 py-2.5 rounded-xl font-black uppercase tracking-wider text-xs transition ${tab === "contests" ? "text-white shadow-lg" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
          style={tab === "contests" ? { background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` } : {}}>
          <Gift size={13} className="inline mr-1.5 -mt-0.5"/> Concours ({contests.length})
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Chargement…</div>
      ) : tab === "pronos" ? (
        data.pronos?.length === 0 ? (
          <EmptyState icon={Target} message="Aucun pronostic encore. Tentez votre chance sur le prochain match !"/>
        ) : (
          <div className="space-y-2">
            {data.pronos.map((p, i) => <PronoRow key={i} prono={p}/>)}
          </div>
        )
      ) : tab === "votes" ? (
        data.votes?.length === 0 ? (
          <EmptyState icon={Award} message="Aucun vote MVP encore. Pendant un match, votez votre joueur préféré sur le Perfs Wall !"/>
        ) : (
          <div className="space-y-2">
            {data.votes.map((v, i) => <VoteRow key={i} vote={v}/>)}
          </div>
        )
      ) : (
        contests.length === 0 ? (
          <EmptyState icon={Gift} message="Aucun concours actif pour le moment. Revenez bientôt !"/>
        ) : (
          <div className="space-y-2">
            {contests.map((c, i) => <ContestRow key={i} contest={c}/>)}
          </div>
        )
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, message, action }) {
  return (
    <div className="rounded-2xl bg-slate-50 border border-slate-100 p-8 text-center">
      <Icon size={32} className="mx-auto text-slate-300 mb-2"/>
      <div className="text-slate-500 text-sm max-w-md mx-auto">{message}</div>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

function PronoRow({ prono }) {
  const isFinished = prono.match_status === "finished";
  const userScore = `${prono.home_pred} - ${prono.away_pred}`;
  const realScore = isFinished ? `${prono.score_home} - ${prono.score_away}` : null;
  return (
    <div className={`rounded-xl p-4 border ${prono.is_winner ? "border-yellow-300 bg-yellow-50" : "border-slate-100 bg-white"} shadow-sm`}>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {prono.home_logo && <img src={prono.home_logo} alt="" className="h-9 w-9 object-contain"/>}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-800 text-sm truncate">
              {prono.home_team || "—"} <span className="text-slate-400 font-normal">vs</span> {prono.away_team || "—"}
            </div>
            <div className="text-[11px] text-slate-500">
              Mon prono : <span className="font-black text-pink-600 tabular-nums">{userScore}</span>
              {realScore && <> · Réel : <span className="font-black tabular-nums" style={{ color: PRIMARY }}>{realScore}</span></>}
            </div>
          </div>
          {prono.away_logo && <img src={prono.away_logo} alt="" className="h-9 w-9 object-contain"/>}
        </div>
        <div className="text-right">
          {prono.is_winner ? (
            <div className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-yellow-200 text-yellow-800 font-black">🏆 Gagnant</div>
          ) : isFinished ? (
            <div className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 font-bold">Terminé</div>
          ) : (
            <div className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-pink-100 text-pink-700 font-bold">À venir</div>
          )}
        </div>
      </div>
    </div>
  );
}

function VoteRow({ vote }) {
  return (
    <div className="rounded-xl p-4 border border-slate-100 bg-white shadow-sm flex items-center gap-3">
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${SECONDARY}20`, color: SECONDARY }}>
        <Award size={18}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-slate-800 text-sm truncate">{vote.player_name || vote.player_slug}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {vote.home_team} vs {vote.away_team} · {vote.voted_at ? new Date(vote.voted_at).toLocaleDateString("fr-FR") : ""}
        </div>
      </div>
    </div>
  );
}

function ContestRow({ contest }) {
  return (
    <div className="rounded-xl p-4 border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-purple-50 shadow-sm">
      <div className="flex items-start gap-3">
        {contest.sponsor_logo && <img src={contest.sponsor_logo} alt={contest.sponsor_name} className="h-12 w-12 object-contain bg-white rounded-lg p-1 border border-slate-200 flex-shrink-0"/>}
        <div className="flex-1 min-w-0">
          <div className="font-black uppercase tracking-wide" style={{ color: PRIMARY }}>{contest.title}</div>
          {contest.sponsor_name && <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">par {contest.sponsor_name}</div>}
          <div className="text-sm text-slate-600 mb-2 leading-relaxed">{contest.description}</div>
          {contest.prize && <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-800 text-[11px] font-bold uppercase tracking-wider"><Trophy size={11}/> {contest.prize}</div>}
          {contest.ends_at && <div className="text-[11px] text-slate-400 mt-2">Jusqu'au {new Date(contest.ends_at).toLocaleDateString("fr-FR")}</div>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// PARTNER BUSINESS HUB — réservé aux partenaires (tags=partner)
// Affiche : annuaire inline + Offres business + Mes demandes + Agenda événements
// ---------------------------------------------------------------
function PartnerBusinessHub() {
  const [offers, setOffers] = useState([]);
  const [contacts, setContacts] = useState({ sent: [], received: [], network: [] });
  const [quoteRequests, setQuoteRequests] = useState([]); // demandes d'offres (legacy)
  const [events, setEvents] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("directory"); // directory | offers | network | quotes | events
  const [requestModal, setRequestModal] = useState(null);
  const [reqMessage, setReqMessage] = useState("");
  const [submittingReq, setSubmittingReq] = useState(false);
  const [contactModal, setContactModal] = useState(null); // partner being invited
  const [contactMessage, setContactMessage] = useState("");
  const [sendingContact, setSendingContact] = useState(false);

  const customerAuthHeaders = () => {
    const token = localStorage.getItem("msc_customer_token");
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const reload = () => {
    const cfg = customerAuthHeaders();
    Promise.all([
      api.get("/sport/business/offers").then((r) => r.data?.rows || []).catch(() => []),
      api.get("/sport/partner/contacts", cfg).then((r) => r.data || { sent: [], received: [], network: [] }).catch(() => ({ sent: [], received: [], network: [] })),
      api.get("/sport/business/offers/me/requests", cfg).then((r) => r.data?.rows || []).catch(() => []),
      api.get("/sport/business/events", cfg).then((r) => r.data?.rows || []).catch(() => []),
      api.get("/sport/partner-directory-secure", cfg).then((r) => r.data?.partners || []).catch(() => []),
    ]).then(([o, c, q, e, d]) => {
      setOffers(o); setContacts(c); setQuoteRequests(q); setEvents(e); setDirectory(d); setLoading(false);
    });
  };
  useEffect(() => { reload(); }, []);

  const submitRequest = async () => {
    if (!requestModal) return;
    setSubmittingReq(true);
    try {
      await api.post(`/sport/business/offers/${requestModal.id}/request`,
        { message: reqMessage }, customerAuthHeaders());
      toast.success("Demande envoyée — l'équipe commerciale vous contactera sous 48h");
      setRequestModal(null); setReqMessage("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSubmittingReq(false); }
  };

  const sendContactRequest = async () => {
    if (!contactModal) return;
    setSendingContact(true);
    try {
      await api.post("/sport/partner/contact-request",
        { to_partner_id: contactModal.id, message: contactMessage },
        customerAuthHeaders());
      toast.success(`Demande envoyée à ${contactModal.name || "ce partenaire"}`);
      setContactModal(null); setContactMessage("");
      reload();
      setSection("network");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSendingContact(false); }
  };

  const respondToContact = async (contactId, action) => {
    try {
      await api.post(`/sport/partner/contacts/${contactId}/${action}`, {}, customerAuthHeaders());
      toast.success(action === "accept" ? "Demande acceptée · vous voyez maintenant ses coordonnées" : "Demande refusée");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const cancelSentRequest = async (contactId) => {
    if (!window.confirm("Annuler cette demande de contact ?")) return;
    try {
      await api.delete(`/sport/partner/contacts/${contactId}`, customerAuthHeaders());
      toast.success("Demande annulée");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const pendingReceived = (contacts.received || []).filter((c) => c.status === "pending");

  return (
    <div className="mb-8" data-testid="partner-business-hub">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-1 h-8 rounded-full" style={{ background: "#f59e0b" }}/>
        <div className="flex-1">
          <h3 className="font-black uppercase italic text-2xl sm:text-3xl text-white" style={{ fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>
            Business Club
          </h3>
          <div className="text-white/60 text-sm">Réseau, offres et événements partenaires.</div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <BusinessQuickLink onClick={() => setSection("directory")} Icon={Briefcase} label="Annuaire" desc={`${directory.length} partenaires`} accent="#f59e0b" testId="partner-quick-network"/>
        <BusinessQuickLink onClick={() => setSection("offers")} Icon={Sparkles} label="Offres business" desc={`${offers.length} dispos`} accent={SECONDARY} testId="partner-quick-offers"/>
        <BusinessQuickLink onClick={() => setSection("network")} Icon={ArrowRight} label="Mon réseau" desc={`${contacts.network.length} connectés${pendingReceived.length ? ` · ${pendingReceived.length} nouvelle(s)` : ""}`} accent="#10b981" testId="partner-quick-network-tab"/>
        <BusinessQuickLink onClick={() => setSection("events")} Icon={Calendar} label="Agenda" desc={`${events.length} événements`} accent="#22d3ee" testId="partner-quick-events"/>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
        <BizTab id="directory" current={section} onClick={setSection} label={`Annuaire (${directory.length})`}/>
        <BizTab id="offers" current={section} onClick={setSection} label={`Offres (${offers.length})`}/>
        <BizTab id="network" current={section} onClick={setSection} label={`Mon réseau (${contacts.network.length}${pendingReceived.length ? ` · ${pendingReceived.length}!` : ""})`}/>
        <BizTab id="conventions" current={section} onClick={setSection} label={`Mes conventions${quoteRequests.length > 0 ? ` (${quoteRequests.length})` : ""}`}/>
        <BizTab id="events" current={section} onClick={setSection} label={`Événements (${events.length})`}/>
      </div>

      {loading ? (
        <div className="text-center py-12 text-white/50 text-sm">Chargement…</div>
      ) : section === "directory" ? (
        directory.length === 0 ? <EmptyState icon={Briefcase} message="Annuaire indisponible (vous devez être partenaire pour y accéder)."/> : (
          <DirectoryGrid partners={directory} onConnect={(p) => { setContactModal(p); setContactMessage(""); }}/>
        )
      ) : section === "offers" ? (
        offers.length === 0 ? <EmptyState icon={Sparkles} message="Aucune offre business disponible pour le moment."/> : (
          <div className="grid sm:grid-cols-2 gap-3">
            {offers.map((o) => <OfferCard key={o.id} offer={o} onRequest={() => setRequestModal(o)}/>)}
          </div>
        )
      ) : section === "network" ? (
        <NetworkSection
          contacts={contacts}
          onAccept={(id) => respondToContact(id, "accept")}
          onDecline={(id) => respondToContact(id, "decline")}
          onCancel={cancelSentRequest}
          onBrowse={() => setSection("directory")}
        />
      ) : section === "conventions" ? (
        <ConventionsSection
          quoteRequests={quoteRequests}
          onBuildRequest={() => setSection("offers")}
        />
      ) : (
        events.length === 0 ? <EmptyState icon={Calendar} message="Aucun événement programmé pour le moment."/> : (
          <div className="grid sm:grid-cols-2 gap-3">
            {events.map((e) => <EventCard key={e.id} event={e} onAfterRsvp={reload}/>)}
          </div>
        )
      )}

      {/* Modal demande d'offre */}
      {requestModal && (
        <RequestOfferModal
          offer={requestModal}
          message={reqMessage}
          setMessage={setReqMessage}
          submitting={submittingReq}
          onSubmit={submitRequest}
          onClose={() => { setRequestModal(null); setReqMessage(""); }}
        />
      )}

      {/* Modal demande de contact (LinkedIn-style) */}
      {contactModal && (
        <ContactRequestModal
          partner={contactModal}
          message={contactMessage}
          setMessage={setContactMessage}
          submitting={sendingContact}
          onSubmit={sendContactRequest}
          onClose={() => { setContactModal(null); setContactMessage(""); }}
        />
      )}
    </div>
  );
}

function BusinessQuickLink({ to, onClick, Icon, label, desc, accent, testId }) {
  const cls = "group relative block rounded-2xl p-4 bg-white shadow-md border-2 hover:scale-[1.02] active:scale-[0.99] transition overflow-hidden text-left w-full";
  const inner = (
    <>
      <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full opacity-15 group-hover:opacity-30 transition" style={{ background: accent, filter: "blur(20px)" }}/>
      <div className="relative">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2" style={{ background: `${accent}20`, color: accent }}>
          <Icon size={18} strokeWidth={2}/>
        </div>
        <div className="font-black uppercase text-[12px] tracking-wide leading-tight" style={{ color: PRIMARY }}>{label}</div>
        <div className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wider">{desc}</div>
      </div>
    </>
  );
  if (to) {
    return <Link to={to} data-testid={testId} className={cls} style={{ borderColor: `${accent}40` }}>{inner}</Link>;
  }
  return <button type="button" onClick={onClick} data-testid={testId} className={cls} style={{ borderColor: `${accent}40` }}>{inner}</button>;
}

function BizTab({ id, current, onClick, label }) {
  const active = current === id;
  return (
    <button onClick={() => onClick(id)} data-testid={`biz-tab-${id}`}
      className={`px-4 py-2 rounded-xl font-black uppercase tracking-wider text-xs whitespace-nowrap transition ${active ? "text-white shadow-lg" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
      style={active ? { background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` } : {}}>
      {label}
    </button>
  );
}

function DirectoryGrid({ partners, onConnect }) {
  const [search, setSearch] = useState("");
  const filtered = partners.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.name || "").toLowerCase().includes(q) || (p.tier || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q);
  });
  return (
    <div data-testid="directory-grid">
      <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="🔍 Rechercher un partenaire (nom, secteur, tier)…"
        className="w-full mb-4 px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur border border-white/20 text-white placeholder-white/50 text-sm focus:outline-none focus:border-pink-400"
        data-testid="directory-search"/>
      <div className="text-[11px] uppercase tracking-wider text-white/50 mb-2">{filtered.length} partenaire{filtered.length > 1 ? "s" : ""}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[600px] overflow-y-auto pr-1">
        {filtered.map((p) => <DirectoryCard key={p.id || p.slug || p.name} partner={p} onConnect={onConnect}/>)}
      </div>
    </div>
  );
}

function DirectoryCard({ partner, onConnect }) {
  const tierColors = { major: "#FFD700", official: "#C0C0C0", premium: "#a855f7", network: "#22d3ee", institutional: "#22d3ee", media: "#ec4899", technical: "#22d3ee" };
  const tierLabels = { major: "Majeur", official: "Officiel", premium: "Premium", network: "Réseau", institutional: "Institutionnel", media: "Média", technical: "Technique" };
  const tier = (partner.tier || "network").toLowerCase();
  const c = tierColors[tier] || "#888";
  const website = partner.website_url || partner.website || "";
  const conn = partner._connection || { status: "none" };
  const isAccepted = conn.status === "accepted";
  const isSelf = conn.status === "self";
  const isPendingSent = conn.status === "pending" && conn.direction === "sent";
  const isPendingReceived = conn.status === "pending" && conn.direction === "received";
  const showContacts = isAccepted || isSelf;

  return (
    <div className="rounded-xl p-4 bg-white border border-slate-200 shadow-sm hover:shadow-md transition flex flex-col" data-testid={`directory-card-${partner.id || partner.slug}`}>
      <div className="flex items-start gap-3">
        {partner.logo_url ? (
          <img src={partner.logo_url} alt={partner.name} className="h-12 w-12 object-contain bg-slate-50 rounded-lg p-1 flex-shrink-0 border border-slate-100"/>
        ) : (
          <div className="h-12 w-12 rounded-lg flex items-center justify-center text-white font-black text-base flex-shrink-0" style={{ background: `linear-gradient(135deg, ${c}, ${PRIMARY})` }}>
            {(partner.name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-black uppercase text-sm leading-tight truncate" style={{ color: PRIMARY }}>{partner.name}</div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold" style={{ background: `${c}25`, color: c }}>{tierLabels[tier] || tier}</span>
            {isAccepted && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-700">✓ Connecté</span>}
            {isPendingSent && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-700">Demande envoyée</span>}
            {isPendingReceived && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold bg-pink-100 text-pink-700">Vous a invité</span>}
          </div>
        </div>
      </div>

      {/* Secteur / adresse */}
      {(partner.sector || partner.address) && (
        <div className="mt-3 space-y-0.5 text-[11px]">
          {partner.sector && <div className="text-slate-500 italic">{partner.sector}</div>}
          {partner.address && <div className="flex items-start gap-1 text-slate-500"><MapPin size={10} className="mt-0.5 shrink-0"/> {partner.address}</div>}
        </div>
      )}

      {/* Description */}
      {partner.description && (
        <p className="mt-2 text-[11px] text-slate-600 leading-relaxed line-clamp-3">{partner.description}</p>
      )}

      {/* Contacts — visibles uniquement si connexion acceptée (LinkedIn-style) */}
      {showContacts && (partner.contact_name || partner.contact_email || partner.contact_phone || partner.email || partner.phone) && (
        <div className="mt-3 pt-2.5 border-t border-slate-100 space-y-1 text-[11px]">
          {partner.contact_name && <div className="font-bold text-slate-700">{partner.contact_name}</div>}
          {(partner.contact_email || partner.email) && (
            <a href={`mailto:${partner.contact_email || partner.email}`} className="flex items-center gap-1.5 text-slate-600 hover:text-pink-600 transition truncate" data-testid={`dir-email-${partner.id}`}>
              <Mail size={10} className="shrink-0"/> {partner.contact_email || partner.email}
            </a>
          )}
          {(partner.contact_phone || partner.phone) && (
            <a href={`tel:${(partner.contact_phone || partner.phone).replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-slate-600 hover:text-pink-600 transition" data-testid={`dir-phone-${partner.id}`}>
              <Phone size={10} className="shrink-0"/> {partner.contact_phone || partner.phone}
            </a>
          )}
        </div>
      )}

      {/* Coordonnées masquées (non connecté) */}
      {!showContacts && (
        <div className="mt-3 pt-2.5 border-t border-slate-100 text-[11px] text-slate-400 italic flex items-start gap-1.5">
          <Lock size={10} className="mt-0.5 shrink-0"/>
          <span>Coordonnées visibles après mise en relation acceptée.</span>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        {website && (
          <a href={website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-pink-600 text-[11px] uppercase tracking-wider font-bold hover:gap-2 transition">
            Visiter <ArrowRight size={11}/>
          </a>
        )}
        {!isSelf && onConnect && conn.status === "none" && (
          <button
            type="button"
            onClick={() => onConnect(partner)}
            data-testid={`partner-connect-${partner.id}`}
            className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full text-white hover:scale-105 transition"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
          >
            <UserPlus size={12}/> Se connecter
          </button>
        )}
        {isPendingSent && (
          <span className="ml-auto text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded-full font-bold">En attente…</span>
        )}
      </div>
    </div>
  );
}

// ---------- Mon réseau (LinkedIn-style) ----------
function NetworkSection({ contacts, onAccept, onDecline, onCancel, onBrowse }) {
  const [tab, setTab] = useState("received");
  const pendingReceived = (contacts.received || []).filter((c) => c.status === "pending");
  const pendingSent = (contacts.sent || []).filter((c) => c.status === "pending");

  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return ""; }
  };

  return (
    <div data-testid="network-section">
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        <button type="button" onClick={() => setTab("received")} className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider whitespace-nowrap transition ${tab === "received" ? "bg-pink-500 text-white" : "bg-white/10 text-white/70 hover:bg-white/15"}`} data-testid="network-tab-received">
          Demandes reçues {pendingReceived.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white text-pink-600 text-[10px]">{pendingReceived.length}</span>}
        </button>
        <button type="button" onClick={() => setTab("sent")} className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider whitespace-nowrap transition ${tab === "sent" ? "bg-pink-500 text-white" : "bg-white/10 text-white/70 hover:bg-white/15"}`} data-testid="network-tab-sent">
          Envoyées ({pendingSent.length})
        </button>
        <button type="button" onClick={() => setTab("network")} className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider whitespace-nowrap transition ${tab === "network" ? "bg-pink-500 text-white" : "bg-white/10 text-white/70 hover:bg-white/15"}`} data-testid="network-tab-network">
          Mon réseau ({contacts.network.length})
        </button>
      </div>

      {tab === "received" && (
        pendingReceived.length === 0 ? (
          <EmptyState icon={Mail} message="Aucune demande de contact en attente. Quand un partenaire vous invite, retrouvez-le ici."/>
        ) : (
          <div className="space-y-2">
            {pendingReceived.map((c) => (
              <div key={c.id} className="rounded-xl bg-white p-4 border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`contact-received-${c.id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {c.from_partner_logo ? (
                    <img src={c.from_partner_logo} alt={c.from_partner_name} className="h-11 w-11 object-contain rounded-lg bg-slate-50 p-1 flex-shrink-0 border border-slate-100"/>
                  ) : (
                    <div className="h-11 w-11 rounded-lg bg-gradient-to-br from-pink-500 to-purple-700 text-white font-black flex items-center justify-center flex-shrink-0">{(c.from_partner_name || "?").charAt(0)}</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-sm uppercase truncate" style={{ color: PRIMARY }}>{c.from_partner_name || "Partenaire"}</div>
                    {c.message ? <div className="text-[12px] text-slate-600 mt-0.5 italic">"{c.message}"</div> : <div className="text-[11px] text-slate-400 italic">Aucun message joint.</div>}
                    <div className="text-[10px] text-slate-400 mt-1">Reçue le {fmt(c.created_at)}</div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button type="button" onClick={() => onAccept(c.id)} data-testid={`contact-accept-${c.id}`} className="px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-black uppercase tracking-wider transition">✓ Accepter</button>
                  <button type="button" onClick={() => onDecline(c.id)} data-testid={`contact-decline-${c.id}`} className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider transition">✕ Refuser</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "sent" && (
        pendingSent.length === 0 ? (
          <EmptyState icon={ArrowRight} message="Aucune demande envoyée en cours." action={<button onClick={onBrowse} className="text-pink-300 text-xs font-bold uppercase tracking-wider hover:text-pink-200">Parcourir l'annuaire →</button>}/>
        ) : (
          <div className="space-y-2">
            {pendingSent.map((c) => (
              <div key={c.id} className="rounded-xl bg-white p-4 border border-slate-200 shadow-sm flex items-center gap-3" data-testid={`contact-sent-${c.id}`}>
                {c.to_partner_logo ? (
                  <img src={c.to_partner_logo} alt={c.to_partner_name} className="h-11 w-11 object-contain rounded-lg bg-slate-50 p-1 flex-shrink-0 border border-slate-100"/>
                ) : (
                  <div className="h-11 w-11 rounded-lg bg-slate-200 text-slate-600 font-black flex items-center justify-center flex-shrink-0">{(c.to_partner_name || "?").charAt(0)}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm uppercase truncate" style={{ color: PRIMARY }}>{c.to_partner_name || "Partenaire"}</div>
                  <div className="text-[11px] text-amber-600 font-bold mt-0.5">⏳ En attente de réponse</div>
                  <div className="text-[10px] text-slate-400">Envoyée le {fmt(c.created_at)}</div>
                </div>
                <button type="button" onClick={() => onCancel(c.id)} className="text-[10px] text-slate-500 hover:text-red-600 uppercase tracking-wider font-bold">Annuler</button>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "network" && (
        contacts.network.length === 0 ? (
          <EmptyState icon={Briefcase} message="Aucun contact accepté pour le moment." action={<button onClick={onBrowse} className="text-pink-300 text-xs font-bold uppercase tracking-wider hover:text-pink-200">Parcourir l'annuaire →</button>}/>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="network-accepted-list">
            {contacts.network.map((p) => <DirectoryCard key={p.id} partner={{ ...p, _connection: { status: "accepted" } }}/>)}
          </div>
        )
      )}
    </div>
  );
}

function ContactRequestModal({ partner, message, setMessage, submitting, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center px-4" style={{ background: "rgba(10,4,16,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="contact-request-modal">
        <div className="flex items-start gap-3 mb-4">
          {partner.logo_url ? (
            <img src={partner.logo_url} alt={partner.name} className="h-12 w-12 object-contain rounded-lg bg-slate-50 p-1 border border-slate-100"/>
          ) : (
            <div className="h-12 w-12 rounded-lg flex items-center justify-center text-white font-black text-base" style={{ background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` }}>{(partner.name || "?").charAt(0)}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Demande de mise en relation</div>
            <h3 className="font-black text-lg uppercase truncate" style={{ color: PRIMARY }}>{partner.name}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          Présentez-vous en quelques mots. Une fois acceptée, vous pourrez voir ses coordonnées (email, téléphone) — et lui les vôtres.
        </p>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          maxLength={500}
          placeholder={`Bonjour, je suis ${partner.name ? "..." : "..."}, je serais intéressé(e) par un échange autour de…`}
          className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-pink-400 resize-none"
          data-testid="contact-message-input"
        />
        <div className="text-[10px] text-slate-400 text-right mt-1">{message.length}/500</div>
        <div className="flex gap-2 mt-4">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50">Annuler</button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            data-testid="contact-submit-btn"
            className="flex-1 px-4 py-2.5 rounded-lg font-black uppercase tracking-wider text-sm text-white disabled:opacity-50 transition hover:scale-[1.02]"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
          >
            {submitting ? "Envoi…" : "Envoyer la demande"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OfferCard({ offer, onRequest }) {
  const catLabels = { visibility: "Visibilité", hospitality: "Hospitalité VIP", advertising: "Publicité", event: "Événementiel" };
  const catColors = { visibility: "#f59e0b", hospitality: "#a855f7", advertising: "#ec4899", event: "#22d3ee" };
  const c = catColors[offer.category] || "#888";
  return (
    <div className="rounded-2xl p-5 bg-white border border-slate-100 shadow-md hover:shadow-xl transition">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold"
          style={{ background: `${c}20`, color: c }}>
          {catLabels[offer.category] || offer.category}
        </div>
        {offer.available_count > 0 && offer.available_count < 5 && (
          <div className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-bold">{offer.available_count} restant{offer.available_count > 1 ? "s" : ""}</div>
        )}
      </div>
      <div className="font-black uppercase text-base tracking-wide mb-1.5 leading-tight" style={{ color: PRIMARY }}>{offer.title}</div>
      <div className="text-sm text-slate-600 mb-3 leading-relaxed">{offer.description}</div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {offer.price_label && <div className="font-black text-pink-600 text-sm">{offer.price_label}</div>}
        <button type="button" onClick={onRequest} data-testid={`offer-request-${offer.id}`}
          className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-black uppercase text-[11px] tracking-wider text-white shadow-md active:scale-95 transition"
          style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
          Demander <ArrowRight size={12}/>
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// CONVENTIONS — Hub unifié : Demandes → Propositions → Validation Président
// → Signature → Active → Archivée
// ============================================================================

/** Mapping des statuts back-office (offer_request.status) vers la timeline
 *  unifiée des conventions affichée au partenaire. */
const CONVENTION_STATUS_FLOW = [
  { key: "submitted",  label: "Demande envoyée",       icon: "📨", color: "#f59e0b", description: "Votre commercial étudie votre demande" },
  { key: "proposed",   label: "Proposition reçue",      icon: "📝", color: "#3b82f6", description: "Le commercial vous a fait une proposition" },
  { key: "validated",  label: "Validée Président",      icon: "✅", color: "#8b5cf6", description: "Approuvée par le président du club" },
  { key: "to_sign",    label: "À signer",               icon: "✍️",  color: "#EC1F7C", description: "Signez le document pour finaliser" },
  { key: "active",     label: "Active · Saison 25/26",  icon: "🎯", color: "#10b981", description: "Convention en cours d'exécution" },
];

/** Mappe l'ancien status backend (`offer_request.status`) au flow timeline. */
function _mapToConventionStatus(req) {
  const s = (req.status || "submitted").toLowerCase();
  if (["submitted", "pending", "new"].includes(s)) return "submitted";
  if (["proposed", "quoted", "offered"].includes(s)) return "proposed";
  if (["validated_president", "validated"].includes(s)) return "validated";
  if (["awaiting_signature", "to_sign", "ready_to_sign"].includes(s)) return "to_sign";
  if (["active", "signed", "confirmed"].includes(s)) return "active";
  if (["archived", "expired", "closed"].includes(s)) return "archived";
  return "submitted";
}

function ConventionsSection({ quoteRequests, onBuildRequest }) {
  const rows = (quoteRequests || []).map((r) => ({ ...r, _status: _mapToConventionStatus(r) }));

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl bg-gradient-to-br from-pink-50 to-purple-50 border border-pink-100 p-8 text-center" data-testid="conventions-empty">
        <div className="text-4xl mb-3">📜</div>
        <h3 className="font-black uppercase text-lg mb-2" style={{ color: PRIMARY }}>Aucune convention pour la saison 2025/2026</h3>
        <p className="text-slate-600 text-sm max-w-md mx-auto mb-4">
          Construisez votre demande en sélectionnant les offres business (packs, visibilité, événements). Le commercial vous reviendra avec une proposition.
        </p>
        <button
          type="button"
          onClick={onBuildRequest}
          data-testid="conventions-build-request-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-black uppercase tracking-wider text-xs hover:scale-105 transition"
          style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
        >
          <Sparkles size={14}/> Construire ma demande
          <ArrowRight size={13}/>
        </button>
      </div>
    );
  }

  // Group rows by status pour les regrouper visuellement
  const grouped = CONVENTION_STATUS_FLOW.map((step) => ({
    ...step,
    items: rows.filter((r) => r._status === step.key),
  }));
  const archived = rows.filter((r) => r._status === "archived");

  return (
    <div className="space-y-6" data-testid="conventions-section">
      {/* Header CTA construire */}
      <div className="rounded-xl bg-white/5 border border-white/15 p-3 flex items-center gap-3">
        <div className="text-2xl">🛒</div>
        <div className="flex-1 min-w-0">
          <div className="font-black uppercase text-sm text-white">Une nouvelle demande à formuler ?</div>
          <div className="text-white/60 text-xs">Construisez votre panier de services à la carte</div>
        </div>
        <button
          type="button"
          onClick={onBuildRequest}
          data-testid="conventions-build-request-cta"
          className="shrink-0 px-3 py-1.5 rounded-full text-white text-xs font-black uppercase tracking-wider hover:scale-105 transition"
          style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
        >
          + Demander
        </button>
      </div>

      {/* Workflow legend */}
      <ConventionWorkflowLegend/>

      {/* Rows by status */}
      {grouped.map((step) => step.items.length > 0 && (
        <div key={step.key} data-testid={`conventions-status-${step.key}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">{step.icon}</span>
            <h4 className="font-black uppercase text-xs tracking-widest" style={{ color: step.color }}>
              {step.label} ({step.items.length})
            </h4>
          </div>
          <div className="space-y-2">
            {step.items.map((r) => <ConventionRow key={r.id} req={r} step={step}/>)}
          </div>
        </div>
      ))}

      {archived.length > 0 && (
        <details className="rounded-xl bg-white/5 border border-white/10" data-testid="conventions-archived">
          <summary className="cursor-pointer px-4 py-3 text-xs uppercase tracking-widest font-black text-white/70 hover:text-white">
            📁 Saisons archivées ({archived.length})
          </summary>
          <div className="px-4 pb-4 space-y-2">
            {archived.map((r) => <ConventionRow key={r.id} req={r} step={{ icon: "📁", color: "#94a3b8", label: "Archivée" }}/>)}
          </div>
        </details>
      )}
    </div>
  );
}

function ConventionWorkflowLegend() {
  return (
    <div className="rounded-xl bg-white p-3 border border-slate-100" data-testid="conventions-workflow">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-2">Cycle de vie d'une convention</div>
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {CONVENTION_STATUS_FLOW.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center text-center shrink-0 w-20">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-base shadow-sm" style={{ background: `${s.color}1f`, color: s.color }}>{s.icon}</div>
              <div className="text-[9px] uppercase tracking-wider mt-1 font-bold leading-tight" style={{ color: s.color }}>{s.label}</div>
            </div>
            {i < CONVENTION_STATUS_FLOW.length - 1 && (
              <div className="flex-1 min-w-[16px] h-0.5 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200"/>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function ConventionRow({ req, step }) {
  const total = req.total_amount || req.amount || null;
  const submittedAt = req.created_at || req.submitted_at;
  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; }
  };
  return (
    <div
      className="rounded-xl bg-white p-4 border border-slate-100 shadow-sm hover:shadow-md transition flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid={`convention-row-${req.id}`}
      style={{ borderLeft: `4px solid ${step.color}` }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-lg">{step.icon}</span>
          <h5 className="font-black uppercase text-sm truncate" style={{ color: PRIMARY }}>
            {req.offer_title || req.title || "Demande de partenariat"}
          </h5>
        </div>
        {req.message && (
          <p className="text-[12px] text-slate-500 italic mt-1 line-clamp-2">"{req.message}"</p>
        )}
        <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          {submittedAt && <span>📅 Envoyée le {fmt(submittedAt)}</span>}
          {req.season && <span>🏆 Saison {req.season}</span>}
          {total != null && <span className="font-bold" style={{ color: PRIMARY }}>{Number(total).toLocaleString("fr-FR")} €</span>}
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider"
          style={{ background: `${step.color}1f`, color: step.color }}
        >
          {step.label}
        </span>
        {req.convention_pdf_url && (
          <a
            href={req.convention_pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] uppercase tracking-wider font-black text-pink-600 hover:text-pink-700 inline-flex items-center gap-1"
          >
            📄 PDF
          </a>
        )}
      </div>
    </div>
  );
}

function RequestRow({ req }) {  const statusLabel = { pending: "En attente", contacted: "Contacté", accepted: "Accepté", declined: "Refusé" };
  const statusColor = { pending: "bg-amber-100 text-amber-700", contacted: "bg-blue-100 text-blue-700", accepted: "bg-green-100 text-green-700", declined: "bg-red-100 text-red-700" };
  return (
    <div className="rounded-xl p-4 border border-slate-100 bg-white shadow-sm flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${SECONDARY}20`, color: SECONDARY }}>
        <Briefcase size={18}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-slate-800 text-sm truncate">{req.offer_title}</div>
        <div className="text-[11px] text-slate-500">Envoyée le {new Date(req.requested_at).toLocaleDateString("fr-FR")}</div>
      </div>
      <div className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-bold ${statusColor[req.status] || "bg-slate-100 text-slate-700"}`}>
        {statusLabel[req.status] || req.status}
      </div>
    </div>
  );
}

function EventCard({ event, onAfterRsvp }) {
  const d = event.event_date ? new Date(event.event_date) : null;
  const deadline = event.rsvp_deadline ? new Date(event.rsvp_deadline) : null;
  const deadlinePassed = deadline ? new Date() > deadline : false;
  const hasGallery = (event.gallery_urls || []).length > 0;
  const rsvpEnabled = event.rsvp_enabled !== false;
  const maxGuests = Math.max(1, Number(event.rsvp_max_guests || 4));
  const my = event.my_rsvp || null;
  const [open, setOpen] = useState(false);
  const [guests, setGuests] = useState(my?.guests_count || 1);
  const [busy, setBusy] = useState(false);

  const submitRsvp = async (status) => {
    setBusy(true);
    try {
      const token = localStorage.getItem("msc_customer_token");
      await api.post(
        `/sport/business/events/${event.id}/rsvp`,
        { status, guests_count: guests, note: "" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        status === "yes"
          ? `Réponse enregistrée : vous + ${guests - 1} invité${guests > 2 ? "s" : ""}`
          : status === "no" ? "Réponse enregistrée : absent" : "Réponse enregistrée : peut-être",
      );
      setOpen(false);
      onAfterRsvp?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const fmtDeadline = (dt) => dt ? dt.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "long" }) : "";

  return (
    <div className="rounded-2xl p-5 bg-white border border-slate-100 shadow-md hover:shadow-xl transition" data-testid={`event-card-${event.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 w-14 text-center rounded-xl py-1.5" style={{ background: `${PRIMARY}10` }}>
          <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: PRIMARY }}>{d ? d.toLocaleDateString("fr-FR", { month: "short" }) : "—"}</div>
          <div className="text-2xl font-black tabular-nums leading-none" style={{ color: PRIMARY, fontFamily: "'Bebas Neue', sans-serif" }}>{d ? d.getDate() : "—"}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-black uppercase text-base tracking-wide leading-tight mb-1" style={{ color: PRIMARY }}>{event.title}</div>
          {event.venue && <div className="text-[11px] text-slate-500 uppercase tracking-wider">📍 {event.venue}</div>}
          {d && (
            <div className="text-[11px] text-slate-500 mt-0.5">
              {d.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })} · {d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>
      </div>

      <div className="text-sm text-slate-600 mb-3 leading-relaxed line-clamp-3">{event.description}</div>

      {/* Date limite de réponse (visible uniquement si RSVP actif & deadline définie) */}
      {rsvpEnabled && deadline && (
        <div className={`text-[11px] font-bold uppercase tracking-wider mb-3 px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1.5 ${deadlinePassed ? "bg-slate-100 text-slate-500" : "bg-amber-50 text-amber-700"}`}>
          <Calendar size={12}/> {deadlinePassed ? "Inscriptions closes" : `Réponse avant le ${fmtDeadline(deadline)}`}
        </div>
      )}

      {/* Statut RSVP existant — badge bien visible */}
      {my && (
        <div className="mb-3" data-testid={`event-my-rsvp-${event.id}`}>
          {my.status === "yes" && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-wider">
              ✓ Vous y serez · {my.guests_count} personne{my.guests_count > 1 ? "s" : ""}
            </div>
          )}
          {my.status === "no" && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold uppercase tracking-wider">
              ✕ Indisponible
            </div>
          )}
          {my.status === "maybe" && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs font-bold uppercase tracking-wider">
              ? Peut-être
            </div>
          )}
        </div>
      )}

      {/* RSVP form ouvert OU bouton "Je réponds" */}
      {rsvpEnabled && !deadlinePassed && (
        open ? (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-3" data-testid={`event-rsvp-form-${event.id}`}>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Nombre de personnes (vous compris) — max {maxGuests}</label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setGuests((g) => Math.max(1, g - 1))} className="w-9 h-9 rounded-lg bg-white border border-slate-200 font-black text-slate-700 hover:bg-pink-50 hover:border-pink-300">−</button>
                <div className="flex-1 text-center font-black text-lg" style={{ color: PRIMARY }} data-testid={`event-rsvp-guests-${event.id}`}>{guests}</div>
                <button type="button" onClick={() => setGuests((g) => Math.min(maxGuests, g + 1))} className="w-9 h-9 rounded-lg bg-white border border-slate-200 font-black text-slate-700 hover:bg-pink-50 hover:border-pink-300">+</button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button type="button" disabled={busy} onClick={() => submitRsvp("yes")} data-testid={`event-rsvp-yes-${event.id}`} className="px-2 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-black uppercase tracking-wider transition disabled:opacity-50">✓ J'y serai</button>
              <button type="button" disabled={busy} onClick={() => submitRsvp("maybe")} data-testid={`event-rsvp-maybe-${event.id}`} className="px-2 py-2 rounded-lg bg-amber-400 hover:bg-amber-500 text-white text-[11px] font-black uppercase tracking-wider transition disabled:opacity-50">? Peut-être</button>
              <button type="button" disabled={busy} onClick={() => submitRsvp("no")} data-testid={`event-rsvp-no-${event.id}`} className="px-2 py-2 rounded-lg bg-slate-300 hover:bg-slate-400 text-slate-800 text-[11px] font-black uppercase tracking-wider transition disabled:opacity-50">✕ Absent</button>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="w-full text-[10px] text-slate-400 hover:text-slate-700 uppercase tracking-widest">Fermer</button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid={`event-rsvp-open-${event.id}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-black uppercase tracking-wider text-xs hover:scale-[1.03] transition"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
          >
            {my ? "Modifier ma réponse" : "Je réponds"}
            <ArrowRight size={13}/>
          </button>
        )
      )}

      {hasGallery && (
        <Link to={`/sport/business/galerie/${event.id}`} className="ml-3 inline-flex items-center gap-1.5 text-pink-600 font-black uppercase text-[11px] tracking-wider hover:gap-2 transition">
          📷 Galerie ({event.gallery_urls.length}) <ArrowRight size={12}/>
        </Link>
      )}
    </div>
  );
}

function RequestOfferModal({ offer, message, setMessage, submitting, onSubmit, onClose }) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="request-offer-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 text-white" style={{ background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` }}>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-80">Demande d'offre business</div>
            <div className="font-black text-xl uppercase italic">{offer.title}</div>
          </div>
          <div className="p-6">
            <div className="text-sm text-slate-600 mb-4 leading-relaxed">{offer.description}</div>
            {offer.price_label && (
              <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-50 text-pink-700 font-black text-xs uppercase tracking-wider">
                💼 {offer.price_label}
              </div>
            )}
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Message à l'équipe commerciale</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1000} rows={4}
              placeholder="Précisez vos besoins, le calendrier souhaité, vos questions…"
              className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 text-sm focus:outline-none focus:border-pink-400 resize-none"
              data-testid="request-message"/>
            <div className="text-[11px] text-slate-400 mt-1">L'équipe IPHB vous contacte sous 48h ouvrées.</div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={onClose} disabled={submitting}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-black uppercase tracking-wider text-xs transition">
                Annuler
              </button>
              <button type="button" onClick={onSubmit} disabled={submitting}
                className="flex-1 py-2.5 rounded-xl font-black uppercase tracking-wider text-xs text-white shadow-lg disabled:opacity-50 transition"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
                data-testid="request-submit">
                {submitting ? "Envoi…" : "Envoyer la demande"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function DashCard({ icon: Icon, title, desc, to, onClick, testId }) {
  const inner = (
    <>
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white shadow mb-2"
        style={{ background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` }}>
        <Icon size={16}/>
      </div>
      <div className="font-black uppercase text-sm" style={{ color: PRIMARY }}>{title}</div>
      <div className="text-slate-500 text-[12px] mt-1">{desc}</div>
      <div className="mt-3 inline-flex items-center gap-1 text-pink-600 text-[11px] font-black uppercase tracking-widest group-hover:gap-2 transition">Accéder <ArrowRight size={11}/></div>
    </>
  );
  const className = "rounded-2xl p-5 bg-white shadow-md border border-slate-100 hover:border-pink-300 hover:shadow-xl transition group text-left w-full";
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className} data-testid={testId}>
        {inner}
      </button>
    );
  }
  return (
    <Link to={to} className={className} data-testid={testId}>
      {inner}
    </Link>
  );
}

function DemoLoginButton({ email, password, label, Icon, accent, testId }) {
  const { login } = useCustomerAuth();
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    setBusy(true);
    try {
      await login(email, password);
      toast.success(`Connecté en tant que démo ${label}`);
    } catch (e) {
      toast.error("Le compte démo est indisponible. Contactez l'administrateur.");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid={testId}
      className="group relative flex flex-col items-start justify-between gap-2 p-3 rounded-2xl text-left bg-white border border-slate-200 shadow-[0_1px_3px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.10)] hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:cursor-wait overflow-hidden"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      {/* Subtle accent halo on hover */}
      <span
        className="pointer-events-none absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-0 group-hover:opacity-30 transition-opacity duration-300 blur-xl"
        style={{ background: accent }}
        aria-hidden="true"
      />

      {/* Icon badge */}
      <div className="flex items-center justify-between w-full relative z-[1]">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-white shadow-sm"
          style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}
        >
          <Icon size={16} strokeWidth={2.4}/>
        </span>
        <ArrowRight
          size={14}
          className="text-slate-300 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all"
        />
      </div>

      {/* Label */}
      <div className="relative z-[1]">
        <div className="text-[9px] font-black tracking-[0.25em] uppercase text-slate-400">
          {busy ? "Connexion…" : "Démo"}
        </div>
        <div className="font-black text-slate-900 leading-none mt-0.5"
          style={{
            fontFamily: "'Bebas Neue', 'Impact', sans-serif",
            fontSize: "1.05rem",
            letterSpacing: "0.04em",
          }}
        >
          {label}
        </div>
      </div>

      {/* Loading bar */}
      {busy && (
        <span
          className="absolute left-0 bottom-0 h-0.5 w-full origin-left animate-pulse"
          style={{ background: accent }}
          aria-hidden="true"
        />
      )}
    </button>
  );
}

/**
 * Bouton accès rapide STAFF (admin/coach/marketing/commercial).
 * Différent de DemoLoginButton : utilise l'auth admin (`/api/auth/login`),
 * stocke `kap_token` puis redirige vers le back-office adapté.
 */
function StaffLoginButton({ email, password, label, sublabel, Icon, accent, redirect, testId }) {
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!r.ok) throw new Error("login failed");
      const data = await r.json();
      if (data?.token && data?.user) {
        // ⚠️ Important : AuthProvider (lib/auth.jsx) requiert AUSSI `kap_user` dans le
        // localStorage pour considérer l'admin connecté. Sans ça, AdminLayout
        // redirige immédiatement vers /admin/login.
        try {
          localStorage.setItem("kap_token", data.token);
          localStorage.setItem("kap_user", JSON.stringify(data.user));
        } catch {}
        toast.success(`Connecté : ${label}`);
        // Redirection après un court délai pour laisser le toast se voir
        setTimeout(() => { window.location.href = redirect; }, 400);
      } else {
        throw new Error("no token");
      }
    } catch (e) {
      toast.error(`Le compte ${label.toLowerCase()} est indisponible. Contactez l'administrateur.`);
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid={testId}
      className="group relative flex flex-col items-start justify-between gap-2 p-3 rounded-2xl text-left bg-slate-900 border border-slate-700 shadow-[0_1px_3px_rgba(0,0,0,0.3)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.4)] hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:cursor-wait overflow-hidden"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <span
        className="pointer-events-none absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-0 group-hover:opacity-40 transition-opacity duration-300 blur-xl"
        style={{ background: accent }}
        aria-hidden="true"
      />
      <div className="flex items-center justify-between w-full relative z-[1]">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-white shadow-sm"
          style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}
        >
          <Icon size={16} strokeWidth={2.4}/>
        </span>
        <ArrowRight size={14} className="text-slate-500 group-hover:text-slate-200 group-hover:translate-x-0.5 transition-all"/>
      </div>
      <div className="relative z-[1]">
        <div className="text-[9px] font-black tracking-[0.25em] uppercase text-slate-500">
          {busy ? "Connexion…" : "Staff"}
        </div>
        <div className="font-black text-white leading-none mt-0.5"
          style={{
            fontFamily: "'Bebas Neue', 'Impact', sans-serif",
            fontSize: "1.05rem",
            letterSpacing: "0.04em",
          }}>
          {label}
        </div>
        {sublabel && <div className="text-[9px] text-slate-500 mt-0.5 italic truncate max-w-[140px]">{sublabel}</div>}
      </div>
      {busy && (
        <span className="absolute left-0 bottom-0 h-0.5 w-full origin-left animate-pulse" style={{ background: accent }} aria-hidden="true"/>
      )}
    </button>
  );
}

